import {
  Admin as RaAdmin,
  CustomRoutes,
  Resource,
  type TranslationMessages,
  resolveBrowserLocale,
  combineDataProviders
} from 'react-admin'
import { useMediaQuery } from '@mui/material'
import polyglotI18nProvider from 'ra-i18n-polyglot'
import { Route, useParams } from 'react-router-dom'
import log from 'loglevel'
import React from 'react'
import { Login } from 'ra-auth-cognito'
import { ja } from './translations-ja'
import { DataProvider } from 'components/DataProvider'
import { CognitoDataProvider } from 'components/CognitoDataProvider'
import { Layout } from 'components/Layout'
import { cognitoAuthProvider } from 'components/Auth'
import { adminTheme, desktopTtheme, otherTheme } from 'components/Theme'
import { endsWith, includes, isNil, last, split, startsWith } from 'lodash/fp'
import { getPartnerId } from 'components/DataProvider/functions'
// import { customTranslates } from './customTranslate'
import { AdminDashboard } from 'components/AdminDashboard'
import fields from './fields.json'
import { MenuBoard } from 'components/Menu'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const translations: Record<string, TranslationMessages> = { ja }

const defaultI18nProvider = polyglotI18nProvider(
  (locale) => translations[locale] as TranslationMessages,
  resolveBrowserLocale(),
  [{ locale: 'ja', name: '日本語' }],
  { allowMissing: true }
)
// const partnerId = 'f698dea9-d49f-43dc-b26d-0096b5bb8c8e'

export const DrawerContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
}>({ open: false, setOpen: (_open: boolean) => {} })
export const PartnerContext = React.createContext<{ partner: string | null }>({
  partner: null
})

export const Admin: React.FC<{ admin?: boolean }> = ({ admin }) => {
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const appCode = localStorage.getItem('appCode')
  const [partnerId, setPartnerId] = React.useState<string | undefined>(
    undefined
  )
  const [open, setOpen] = React.useState(false)
  const { partner: partnerParam } = useParams()
  const code = partnerParam ?? appCode
  const base = import.meta.env.BASE_URL === '/' ? '' : import.meta.env.BASE_URL
  React.useEffect(() => {
    if (!isNil(partnerId)) return
    if (isNil(code)) return
    if (code === 'menu') {
      const code = last(split('/')(window.location.pathname)) as string
      // @ts-expect-error ts(2322)
      window.location = `${base}/menu/${code}`
      return
    }
    getPartnerId(code)
      .then((id: string | undefined) => {
        if (isNil(id)) return
        setPartnerId(id)
        localStorage.setItem('appCode', code)
      })
      .catch(log.error)
  }, [partnerParam, code])
  if (isNil(partnerId)) return
  const authProvider = cognitoAuthProvider(partnerId)
  if (isNil(authProvider)) return null
  const dataProvider = combineDataProviders((resource: string) => {
    const restDataProvider = DataProvider(partnerId)
    if (includes(resource)(['accounts', 'mappings', 'mappingAlls'])) {
      return CognitoDataProvider(partnerId)
    }
    return restDataProvider
  })
  if (
    (isNil(admin) || !admin) &&
    !startsWith(`${base}/menu/${code as string}`)(window.location.pathname) &&
    window.location.pathname !== `${base}/login`
  ) {
    // @ts-expect-error ts(2322)
    window.location = `${base}/menu/${code as string}`
  }
  const i18nProvider = {
    ...defaultI18nProvider,
    translate: (key: string, options: Record<string, any>): string => {
      const { translate } = defaultI18nProvider
      if (!isNil(partnerId)) {
        const { name, code, area, office } = fields[partnerId] ?? {
          name: '事業所',
          code: '事業所コード',
          area: '支店',
          office: '営業所'
        }
        //   return customTranslates(
        //     partnerId,
        //     key,
        //     { ...options, name, code },
        //     translate
        //   )
        return translate(key, { ...options, name, code, area, office })
      } else {
        return translate(key, {
          ...options,
          name: '事業所',
          code: '事業所コード',
          area: '支店',
          office: '営業所'
        })
      }
    }
  }
  return (
    <PartnerContext.Provider value={{ partner: partnerId }}>
      <DrawerContext.Provider value={{ open, setOpen }}>
        {isNil(admin) || !admin ? (
          endsWith('/admin')(window.location.pathname) ? (
            <RaAdmin
              dataProvider={dataProvider}
              i18nProvider={i18nProvider}
              authProvider={authProvider}
              loginPage={Login}
              requireAuth
              layout={Layout}
              theme={adminTheme}>
              <CustomRoutes>
                <Route
                  path="/admin"
                  element={<MenuBoard partner={partnerId} admin="menu" />}
                />
                <Route
                  path="/admin/accounts/admin"
                  element={<MenuBoard partner={partnerId} admin="accounts" />}
                />
                <Route
                  path="/admin/spots/admin"
                  element={<MenuBoard partner={partnerId} admin="spots" />}
                />
                <Route
                  path="/admin/announces/admin"
                  element={<MenuBoard partner={partnerId} admin="announces" />}
                />
                <Route
                  path="/admin/data/admin"
                  element={<MenuBoard partner={partnerId} admin="data" />}
                />
                <Route
                  path="/admin/demand/admin"
                  element={<MenuBoard partner={partnerId} admin="demand" />}
                />
                <Route
                  path="/admin/loginLog/admin"
                  element={<MenuBoard partner={partnerId} admin="loginLog" />}
                />
                <Route
                  path="/admin/commentLog/admin"
                  element={<MenuBoard partner={partnerId} admin="commentLog" />}
                />
              </CustomRoutes>
            </RaAdmin>
          ) : (
            <RaAdmin
              dataProvider={dataProvider}
              i18nProvider={i18nProvider}
              authProvider={authProvider}
              loginPage={Login}
              requireAuth
              layout={Layout}
              lightTheme={isDesktop ? desktopTtheme : otherTheme}>
              <CustomRoutes>
                <Route
                  path="/spot/:target"
                  element={<MenuBoard partner={partnerId} spot={true} />}
                />
                <Route
                  path="/spot/demand/:target/:date/:kind"
                  element={<MenuBoard partner={partnerId} spot={true} />}
                />
                <Route
                  path="/dashboard"
                  element={<MenuBoard partner={partnerId} dashboard={true} />}
                />
                <Route
                  path="/spot"
                  element={<MenuBoard partner={partnerId} spot={true} />}
                />
                <Route
                  path="/allData"
                  element={<MenuBoard partner={partnerId} allData={true} />}
                />
                <Route path="/" element={<MenuBoard partner={partnerId} />} />
              </CustomRoutes>
              <Resource name="organizations" />
              <Resource name="summarys" />
              <Resource name="accounts" />
            </RaAdmin>
          )
        ) : (
          <RaAdmin
            dataProvider={dataProvider}
            i18nProvider={i18nProvider}
            authProvider={authProvider}
            loginPage={Login}
            requireAuth
            layout={Layout}
            theme={adminTheme}>
            <CustomRoutes>
              <Route
                path="/"
                element={<AdminDashboard partner={partnerId} common={true} />}
              />
            </CustomRoutes>
          </RaAdmin>
        )}
      </DrawerContext.Provider>
    </PartnerContext.Provider>
  )
}
